/**
 * Page Layout Processor
 * Handles page dividers, headers/footers, and page images in formatted documents
 */

import { logger } from '../../core/logger';

export interface PageLayoutProcessorOptions {
  uiManager: any; // UIManager instance for translations
}

export class PageLayoutProcessor {
  private options: PageLayoutProcessorOptions;

  constructor(options: PageLayoutProcessorOptions) {
    this.options = options;
  }

  /**
   * Process complete page layout for a document container
   * @param container - Container with formatted HTML
   * @param doclingJson - Parsed Docling JSON data
   * @param hasImages - Whether HTML already contains images
   */
  processLayout(
    container: HTMLElement,
    doclingJson: any,
    hasImages: boolean
  ): void {
    // First, add basic page dividers if .page elements exist
    this.processPageDividers(container);

    // Then process provenance-based dividers and headers/footers
    if (doclingJson?.texts && Array.isArray(doclingJson.texts)) {
      this.insertPageDividersFromProvenance(container, doclingJson.texts);
      this.insertHeadersAndFooters(container, doclingJson.texts);
    }

    // Finally, add page images if needed
    if (doclingJson?.pages && !hasImages) {
      this.processPageImages(container, doclingJson.pages);
    }
  }

  /**
   * Add page dividers between page elements
   * @param container - Container element
   */
  processPageDividers(container: HTMLElement): void {
    const pageElements = container.querySelectorAll('.page');

    if (pageElements.length > 1) {
      // Insert dividers between pages (starting from the second page)
      for (let i = 1; i < pageElements.length; i++) {
        const divider = document.createElement('div');
        divider.className = 'page-divider';

        const pageNumber = document.createElement('span');
        pageNumber.className = 'page-number';
        pageNumber.textContent = `${this.options.uiManager.t('page.label')} ${i + 1}`;

        divider.appendChild(pageNumber);

        // Insert divider before the current page element
        const currentPage = pageElements[i];
        if (currentPage) {
          const parent = currentPage.parentNode;
          parent?.insertBefore(divider, currentPage);
        }
      }
    }
  }

  /**
   * Create a page divider element
   * @param pageNumber - Page number to display
   */
  private createPageDivider(pageNumber: number): HTMLElement {
    const divider = document.createElement('div');
    divider.className = 'page-divider';

    const pageNum = document.createElement('span');
    pageNum.className = 'page-number';
    pageNum.textContent = `${this.options.uiManager.t('page.label')} ${pageNumber}`;

    divider.appendChild(pageNum);
    return divider;
  }

  /**
   * Insert page dividers based on provenance data in text elements
   * @param container - Container with formatted HTML
   * @param texts - Array of text elements from Docling JSON
   */
  insertPageDividersFromProvenance(container: HTMLElement, texts: any[]): void {
    // Extract page numbers from provenance data
    const textsByPage = new Map<number, any[]>();
    let hasProvData = false;

    texts.forEach((textElement) => {
      if (
        textElement.prov &&
        Array.isArray(textElement.prov) &&
        textElement.prov.length > 0
      ) {
        hasProvData = true;
        // Provenance entries contain page information
        textElement.prov.forEach((provEntry: any) => {
          // Check for page_no (standard field name in DoclingDocument)
          if (provEntry.page_no !== undefined) {
            const pageNum = provEntry.page_no;
            if (!textsByPage.has(pageNum)) {
              textsByPage.set(pageNum, []);
            }
            textsByPage.get(pageNum)?.push(textElement);
          }
        });
      }
    });

    if (!hasProvData) {
      logger.warn(
        'No provenance data found in Docling JSON - page dividers cannot be inserted.'
      );
      logger.debug(
        'DOCX files: Docling-serve does not provide page boundary information for Word documents.'
      );
      logger.debug(
        'PDF files: Page dividers will appear automatically based on provenance data.'
      );
      logger.debug(
        'This is a limitation of how docling-serve processes different document formats.'
      );
      return;
    }

    // If we found page information, try to insert dividers
    if (textsByPage.size > 1) {
      logger.debug(`Found ${textsByPage.size} pages in provenance data`);
      const sortedPages = Array.from(textsByPage.keys()).sort((a, b) => a - b);
      logger.debug('Page numbers:', sortedPages);

      // Try to match text content to HTML elements and insert dividers
      const allParagraphs = Array.from(
        container.querySelectorAll('p, h1, h2, h3, h4, h5, h6')
      );
      // #159: read every element's text once, not once per page searched.
      // Dividers go in as siblings, so the texts stay valid.
      const paragraphTexts = allParagraphs.map(
        (elem) => elem.textContent ?? ''
      );
      logger.debug(`Found ${allParagraphs.length} HTML elements to search`);

      let dividersInserted = 0;
      sortedPages.forEach((pageNum, index) => {
        if (index === 0) return; // Skip first page

        const pageTexts = textsByPage.get(pageNum);
        if (!pageTexts || pageTexts.length === 0) return;

        // Find the first text element on this page
        const firstTextOnPage = pageTexts[0];
        const searchText = firstTextOnPage.text || firstTextOnPage.orig;

        if (!searchText) {
          logger.warn(`Page ${pageNum}: No search text found`);
          return;
        }

        logger.debug(
          `Page ${pageNum}: Searching for "${searchText.substring(0, 30)}..."`
        );

        // Find the corresponding HTML element
        const elem =
          allParagraphs[
            paragraphTexts.findIndex((text) => text.includes(searchText))
          ];
        if (elem) {
          const divider = this.createPageDivider(pageNum);
          elem.parentNode?.insertBefore(divider, elem);
          dividersInserted++;
          logger.debug(`Page ${pageNum}: Divider inserted`);
        } else {
          logger.warn(
            `Page ${pageNum}: Could not find matching HTML element for "${searchText.substring(0, 30)}..."`
          );
        }
      });

      logger.debug(`Total dividers inserted: ${dividersInserted}`);
    }
  }

  /**
   * Group furniture items (headers/footers) by page number
   * @param items - Array of text items
   * @param labelFilter - Label to filter by (e.g., 'header' or 'footer')
   */
  private groupFurnitureByPage(
    items: any[],
    labelFilter: string
  ): Map<number, any[]> {
    const byPage = new Map<number, any[]>();

    items
      .filter(
        (item) =>
          item.content_layer === 'furniture' &&
          item.label &&
          item.label.includes(labelFilter)
      )
      .forEach((item) => {
        if (
          item.prov &&
          item.prov.length > 0 &&
          item.prov[0].page_no !== undefined
        ) {
          const pageNo = item.prov[0].page_no;
          if (!byPage.has(pageNo)) {
            byPage.set(pageNo, []);
          }
          byPage.get(pageNo)!.push(item);
        }
      });

    return byPage;
  }

  /**
   * Insert headers and footers at appropriate page positions
   * @param container - Container with formatted HTML
   * @param texts - Array of text elements from Docling JSON
   */
  insertHeadersAndFooters(container: HTMLElement, texts: any[]): void {
    // Group headers and footers by page
    const headersByPage = this.groupFurnitureByPage(texts, 'header');
    const footersByPage = this.groupFurnitureByPage(texts, 'footer');

    const headerCount = Array.from(headersByPage.values()).reduce(
      (sum, arr) => sum + arr.length,
      0
    );
    const footerCount = Array.from(footersByPage.values()).reduce(
      (sum, arr) => sum + arr.length,
      0
    );

    logger.debug(`Found ${headerCount} headers and ${footerCount} footers`);

    if (headerCount === 0 && footerCount === 0) {
      return;
    }

    // Get all page dividers that were inserted
    const pageDividers = container.querySelectorAll('.page-divider');

    pageDividers.forEach((divider) => {
      // Extract page number from divider text (e.g., "Page 2" or localized equivalent)
      const pageText = divider.querySelector('.page-number')?.textContent;
      // Match pattern like "Page 2", "Seite 2", "Página 2", etc.
      const pageMatch = pageText?.match(/\d+$/);
      if (!pageMatch) return;

      const pageNo = parseInt(pageMatch[0]);

      // Insert header after the divider
      if (headersByPage.has(pageNo)) {
        const pageHeaders = headersByPage.get(pageNo)!;
        pageHeaders.forEach((header) => {
          const headerEl = document.createElement('div');
          headerEl.className = 'page-header';
          headerEl.textContent = header.text || header.orig || '';
          divider.parentNode?.insertBefore(headerEl, divider.nextSibling);
        });
      }

      // Insert footer before the next divider (or at the end)
      if (footersByPage.has(pageNo)) {
        const pageFooters = footersByPage.get(pageNo)!;
        const nextDivider = this.findNextPageDivider(divider as HTMLElement);

        pageFooters.forEach((footer) => {
          const footerEl = document.createElement('div');
          footerEl.className = 'page-footer';
          footerEl.textContent = footer.text || footer.orig || '';

          if (nextDivider) {
            nextDivider.parentNode?.insertBefore(footerEl, nextDivider);
          } else {
            // Last page - insert at the end
            container.appendChild(footerEl);
          }
        });
      }
    });

    // Handle first page (no divider before it)
    if (headersByPage.has(1)) {
      const firstHeaders = headersByPage.get(1)!;
      firstHeaders.forEach((header) => {
        const headerEl = document.createElement('div');
        headerEl.className = 'page-header';
        headerEl.textContent = header.text || header.orig || '';
        container.insertBefore(headerEl, container.firstChild);
      });
    }

    if (footersByPage.has(1)) {
      const firstFooters = footersByPage.get(1)!;
      const firstDivider = pageDividers[0] as HTMLElement | undefined;

      firstFooters.forEach((footer) => {
        const footerEl = document.createElement('div');
        footerEl.className = 'page-footer';
        footerEl.textContent = footer.text || footer.orig || '';

        if (firstDivider) {
          firstDivider.parentNode?.insertBefore(footerEl, firstDivider);
        } else {
          // Only one page
          container.appendChild(footerEl);
        }
      });
    }

    logger.debug('Headers and footers inserted');
  }

  /**
   * Find the next page divider after the given element
   * @param currentDivider - Current page divider
   * @returns Next page divider or null
   */
  private findNextPageDivider(currentDivider: HTMLElement): HTMLElement | null {
    let next = currentDivider.nextSibling;
    while (next) {
      if (
        (next as HTMLElement).classList &&
        (next as HTMLElement).classList.contains('page-divider')
      ) {
        return next as HTMLElement;
      }
      next = next.nextSibling;
    }
    return null;
  }

  /**
   * Process and display page images
   * @param container - Container element
   * @param pages - Pages data from docling
   */
  processPageImages(
    container: HTMLElement,
    pages: any[] | Record<string, any>
  ): void {
    // Convert pages to array if it's an object
    const pagesArray = Array.isArray(pages) ? pages : Object.values(pages);

    // Check if pages contain images
    pagesArray.forEach((page, pageIndex) => {
      if (page?.image?.uri) {
        // Add page divider before each page (except the first one)
        if (pageIndex > 0) {
          const divider = document.createElement('div');
          divider.className = 'page-divider';

          const pageNumber = document.createElement('span');
          pageNumber.className = 'page-number';

          // Try to get page number from page object, fallback to index
          const pageNo =
            page.page_no || page.page_number || page.pageNo || pageIndex + 1;
          pageNumber.textContent = `${this.options.uiManager.t('page.label')} ${pageNo}`;

          divider.appendChild(pageNumber);
          container.appendChild(divider);
        }

        // Fix malformed data URI (docling-serve bug: uses : instead of ; before base64)
        let imageUri = page.image.uri;
        if (imageUri.includes('data:image/') && imageUri.includes(':base64')) {
          imageUri = imageUri.replace(/:base64/g, ';base64');
        }

        // Only allow data URIs from parsed documents
        const imgElement = document.createElement('img');
        if (imageUri.startsWith('data:image/')) {
          imgElement.src = imageUri;
          imgElement.alt = `Page ${pageIndex + 1}`;
        } else {
          imgElement.alt = 'Image blocked (external URI not allowed)';
        }
        imgElement.style.maxWidth = '100%';
        imgElement.style.height = 'auto';
        imgElement.style.marginBottom = '1rem';

        container.appendChild(imgElement);
      }
    });

    // Also check for images already in the HTML and ensure they have max-width
    const images = container.querySelectorAll('img');
    images.forEach((img) => {
      (img as HTMLImageElement).style.maxWidth = '100%';
      (img as HTMLImageElement).style.height = 'auto';
    });
  }
}
